import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import PageTour from '@/components/PageTour';
import ShareViewButton from '@/components/common/ShareViewButton';
import {
  BarChart3, Zap, TrendingUp, Activity, Brain, Globe, Download, ShieldCheck, ChevronRight, Banknote, Users, CreditCard,
} from 'lucide-react';

const SECTIONS = [
  {
    title: 'Customer Reports',
    accent: 'bg-rose-500',
    items: [
      { path: '/customer-reports', icon: Users, label: 'Customer Reports', desc: 'Ledger · Aging · Sales · Top Customers — date range & export', color: 'text-rose-600', bg: 'bg-rose-50', ring: 'ring-rose-100', badge: 'New' },
      { path: '/credit-report', icon: CreditCard, label: 'Credit Report', desc: 'Customer credit balances & aging', color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
    ],
  },
  {
    title: 'Dashboards',
    accent: 'bg-violet-500',
    items: [
      { path: '/bi-dashboard', icon: BarChart3, label: 'BI Dashboard', desc: 'Business intelligence overview', color: 'text-violet-600', bg: 'bg-violet-50', ring: 'ring-violet-100' },
      { path: '/ai-hub', icon: Brain, label: 'AI Hub', desc: 'AI-powered insights & predictions', color: 'text-indigo-600', bg: 'bg-indigo-50', ring: 'ring-indigo-100' },
      { path: '/detailed-report', icon: Banknote, label: 'Cash & Bank Report', desc: 'Cash, bank & credit breakdown', color: 'text-success', bg: 'bg-success/10', ring: 'ring-emerald-100' },
    ],
  },
  {
    title: 'Analytics & Forecasting',
    accent: 'bg-info',
    items: [
      { path: '/analytics', icon: BarChart3, label: 'Analytics', desc: 'Sales & performance analytics', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/advanced-analytics', icon: Zap, label: 'Advanced Analytics', desc: 'Deep-dive data analysis', color: 'text-warning', bg: 'bg-warning/10', ring: 'ring-amber-100' },
      { path: '/enhanced-pnl', icon: TrendingUp, label: 'Enhanced P&L', desc: 'Enhanced profit & loss view', color: 'text-success', bg: 'bg-success/10', ring: 'ring-emerald-100' },
      { path: '/visualizations', icon: Activity, label: 'Visualizations', desc: 'Data charts & graphs', color: 'text-violet-600', bg: 'bg-violet-50', ring: 'ring-violet-100' },
      { path: '/sales-forecast', icon: TrendingUp, label: 'Sales Forecast', desc: 'AI-powered sales prediction', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/anomaly-detection', icon: Zap, label: 'Anomaly Detection', desc: 'Detect unusual patterns', color: 'text-error', bg: 'bg-error/10', ring: 'ring-red-100' },
    ],
  },
  {
    title: 'Data & Export',
    accent: 'bg-success',
    items: [
      { path: '/cross-branch-report', icon: Globe, label: 'Cross-Branch Report', desc: 'Multi-branch comparison', color: 'text-success', bg: 'bg-success/10', ring: 'ring-emerald-100' },
      { path: '/export-center', icon: Download, label: 'Export Center', desc: 'Download reports & data', color: 'text-muted-foreground', bg: 'bg-muted/50', ring: 'ring-border' },
      { path: '/data-integrity', icon: ShieldCheck, label: 'Data Integrity', desc: 'Data validation & checks', color: 'text-info', bg: 'bg-info/10', ring: 'ring-blue-100' },
      { path: '/zatca-reporting-log', icon: ShieldCheck, label: 'ZATCA Reporting Log', desc: 'E-invoice submissions & ZATCA errors', color: 'text-emerald-600', bg: 'bg-emerald-50', ring: 'ring-emerald-100' },
    ],
  },
];

function FeatureCard({ item, navigate }) {
  return (
    <button
      onClick={() => navigate(item.path)}
      className="group flex items-center gap-3.5 p-4 rounded-xl bg-white border border-border hover:border-border hover:shadow-md transition-all duration-200 text-left"
      data-testid={`nav-${item.path.replace('/', '')}`}
    >
      <div className={`w-10 h-10 rounded-xl ${item.bg} ${item.color} flex items-center justify-center shrink-0 ring-1 ${item.ring} group-hover:scale-110 transition-transform duration-200`}>
        <item.icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="font-medium text-foreground text-sm group-hover:text-violet-700 transition-colors">{item.label}</p>
          {item.badge && <span className="px-1.5 py-0.5 bg-rose-500 text-white text-[9px] font-bold uppercase rounded">{item.badge}</span>}
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{item.desc}</p>
      </div>
      <ChevronRight size={14} className="text-stone-200 group-hover:text-muted-foreground shrink-0 group-hover:translate-x-0.5 transition-all" />
    </button>
  );
}

export default function ReportsModulePage() {
  const navigate = useNavigate();
  return (
    <DashboardLayout module="reports">
      <div className="space-y-10 max-w-6xl mx-auto" data-testid="reports-module-page">
        {/* Header */}
        <div className="relative">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-600 to-purple-700 flex items-center justify-center shadow-lg shadow-violet-200/50">
              <BarChart3 size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">Reports & Analytics</h1>
              <p className="text-sm text-muted-foreground mt-0.5">BI dashboards, AI insights, forecasting & data visualization</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <ShareViewButton params={{}} />
            <PageTour storageKey="tour_reports" steps={[
              { target: '[data-testid="reports-module-page-2"]', title: 'Reports Hub', content: 'Central hub for all BI dashboards, analytics, forecasting, and data export tools.' },
            ]} />
          </div>
        </div>

        {/* Sections */}
        {SECTIONS.map((section, si) => (
          <div key={section.title} style={{ animationDelay: `${si * 60}ms` }}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-1 h-5 rounded-full ${section.accent}`} />
              <h2 className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{section.title}</h2>
              <div className="flex-1 h-px bg-muted" />
              <span className="text-[10px] text-muted-foreground font-medium">{section.items.length} items</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {section.items.map((item) => (
                <FeatureCard key={item.path} item={item} navigate={navigate} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}
